/**
 * Firebase Configuration — Lazy Initialization
 *
 * CIRCADIA-PERF: Firebase SDK is NOT initialized at import time.
 * Instead, getApp(), getDB(), getFirebaseAuthInstance(), and getFunctionsInstance()
 * return singletons that are created on first call.
 *
 * This means zero Firebase network connections until a feature actually needs one.
 * When done with Firestore (e.g. user leaves chat screen), call releaseDB()
 * to terminate the persistent connection and free resources.
 *
 * To fully restore eager init: replace this file with the git HEAD version.
 */

import type { FirebaseApp } from "firebase/app";
import type { Firestore } from "firebase/firestore";
import type { Auth } from "firebase/auth";
import type { Functions } from "firebase/functions";

const env = import.meta.env;

const firebaseConfig = {
  apiKey:
    env.VITE_FIREBASE_API_KEY || "AIzaSyCX_yVkdFzhQyG9jtvuVfQ7ez-fW27apoo",
  authDomain:
    env.VITE_FIREBASE_AUTH_DOMAIN || "circadia-patient-app.firebaseapp.com",
  projectId: env.VITE_FIREBASE_PROJECT_ID || "circadia-patient-app",
  storageBucket:
    env.VITE_FIREBASE_STORAGE_BUCKET ||
    "circadia-patient-app.firebasestorage.app",
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID || "253297847539",
  appId:
    env.VITE_FIREBASE_APP_ID || "1:253297847539:web:d0e97745d4b28895335b86",
};

let _app: FirebaseApp | null = null;
let _db: Firestore | null = null;
let _auth: Auth | null = null;
let _functions: Functions | null = null;

/**
 * Lazy singleton — Firebase App.
 * Created on first call, reused thereafter.
 */
export async function getApp(): Promise<FirebaseApp> {
  if (!_app) {
    const { initializeApp } = await import("firebase/app");
    _app = initializeApp(firebaseConfig);
  }
  return _app;
}

/**
 * Lazy singleton — Firestore instance.
 * Opens a persistent connection (WebSocket or long-poll).
 * Call releaseDB() when you're done to close it.
 */
export async function getDB(): Promise<Firestore> {
  if (!_db) {
    const app = await getApp();
    const { getFirestore } = await import("firebase/firestore");
    _db = getFirestore(app);
  }
  return _db;
}

/**
 * Terminate the Firestore connection and release resources.
 * Next call to getDB() will create a fresh instance.
 */
export async function releaseDB(): Promise<void> {
  if (_db) {
    try {
      const { terminate } = await import("firebase/firestore");
      await terminate(_db);
    } catch {
      // Already terminated or never connected
    }
    _db = null;
  }
}

/**
 * Lazy singleton — Firebase Auth.
 */
export async function getFirebaseAuthInstance(): Promise<Auth> {
  if (!_auth) {
    const app = await getApp();
    const { getAuth } = await import("firebase/auth");
    _auth = getAuth(app);
  }
  return _auth;
}

/**
 * Lazy singleton — Cloud Functions handle.
 * Does NOT open a persistent connection (HTTP-based).
 */
export async function getFunctionsInstance(): Promise<Functions> {
  if (!_functions) {
    const app = await getApp();
    const { getFunctions } = await import("firebase/functions");
    _functions = getFunctions(app, "us-central1");
  }
  return _functions;
}

// ---------------------------------------------------------------------------
// Backward-compatible synchronous exports
// These exist so files that do `import { db } from "./config"` don't break
// at compile time. They are Proxy objects that throw at runtime if used
// without first calling the async getters above.
//
// Modules that have been updated to use the async getters don't need these.
// ---------------------------------------------------------------------------

const NO_INIT_MSG =
  "Firebase not initialized. Use the async getter (getDB/getFirebaseAuthInstance) instead.";

export const db = new Proxy({} as Firestore, {
  get(_, prop) {
    if (prop === "type") return "firestore";
    if (typeof prop === "symbol") return undefined;
    throw new Error(NO_INIT_MSG);
  },
});

export const firebaseAuth = new Proxy({} as Auth, {
  get(_, prop) {
    if (prop === "currentUser") return _auth?.currentUser ?? null;
    if (typeof prop === "symbol") return undefined;
    if (_auth) return (_auth as any)[prop];
    throw new Error(NO_INIT_MSG);
  },
});

export const app = null as unknown as FirebaseApp;
export const functions = null as unknown as Functions;
